import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnResizeBar from "@shared/components/SnResizeBar";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_PRD_BAS_REG_021 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });
  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [gridFocus, setGridFocus] = useState(false);
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_PRD_BAS_REG_021";
  const defaultSize = { panel1width: "30.0%", panel2width: "70.0%" };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
  //#endregion

  //#region Initialize
  useEffect(() => {
    const fetchform = async () => {
      try {
        const defaultForm = {
          cd_biz: userInfo.cd_biz,
        };
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항
        setFormData({ ...rtn.formData, ...defaultForm }); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region OnSearch
  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();

    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const params = {
        id: "sn_prd_bas_reg_021_01",
        params: searchRef.current.getFormdata(),
      };
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1],
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  //#region On Insert

  const onInsert = async () => {
    try {
      return gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {
      return false;
    }
  };

  //#region On Delete
  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  //#region On AddRow
  const onAddRow = () => {
    try {
      return gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  //#region On DeleteRow
  const onDeleteRow = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
    gridRefs.current["grid2"].current?.deleteSelectedRows();
  };

  //#region BeforeAddL
  const onBeforeAddRowL = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");

      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        no_line: maxNoLine + 1,
        cd_rout: focusedNodeH.data?.cd_rout,
        tp_rout: userInfo.tp_rout,
      };
    } catch (error: any) {
      return false;
    }
  };

  //#region  OnSave
  const onSave = async () => {
    let prdRoutChanges;
    let prdDowncodeChanges;

    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }

      //L 필수값 체크
      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return;
      }

      prdRoutChanges = gridRefs.current["grid1"].current.getChange();
      prdDowncodeChanges = gridRefs.current["grid2"].current.getChange();
      if (
        prdRoutChanges.modifiedRowcount === 0 &&
        prdDowncodeChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }
      const SaveData = {
        prd_rout: prdRoutChanges,
        prd_downcode: prdDowncodeChanges,
      };
      await fnSave("SN_PRD_BAS_REG_021_SAVE_01", SaveData);
      gridRefs.current["grid1"].current?.resetUpdate();
      gridRefs.current["grid2"].current?.resetUpdate();
    } catch (error: any) {}
  };
  //#endregion

  //#region Filter
  // const onFocusedRowChanged = (event: any) => {
  //   const selectedRow = event;
  //   if (selectedRow) {
  //     const cd_rout = selectedRow.cd_rout;
  //     gridRefs.current["grid2"].current?.applyFilters({ cd_rout: cd_rout });
  //     gridRefs.current["grid2"].current?.selectAndFocusRow(0);
  //   } else {
  //     return;
  //   }
  // };
  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onBtnDeleteClick: onDeleteRow,
          onBtnAddClick: onAddRow,
        },
      },
    }),
    [columns, dataSources]
  );

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div>
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          
        ></SearchPanel>
      </div>
      <div className="flex w-full h-full min-w-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="29.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "공정경로정보" }}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["cd_rout"] }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="70%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "single", title: "비가동정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_PRD_BAS_REG_021;
